
"use client";

import React, { memo, useCallback, useEffect, useState } from "react";
import { ReactFlow, Background, Controls, Edge, Node, Connection, ConnectionMode, OnSelectionChangeParams } from '@xyflow/react';
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { getAccountOwnershipInfo } from "./accountService";
import { 
  AlertDialog, 
  AlertDialogContent, 
  AlertDialogHeader, 
  AlertDialogTitle, 
  AlertDialogDescription, 
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction
} from "@/components/ui/alert-dialog";

interface FlowComponentProps {
  nodes: Node[];
  edges: Edge[];
  onNodesChange: (changes: any) => void;
  onEdgesChange: (changes: any) => void;
  onConnect: (params: Connection) => void;
  nodeTypes: Record<string, React.ComponentType<any>>;
  flowHeight: number;
  onSelectionChange?: (params: OnSelectionChangeParams) => void;
  selectedAccount?: { company_id?: string } | null;
}

const FlowComponent = memo(({
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  onConnect,
  nodeTypes,
  flowHeight,
  onSelectionChange,
  selectedAccount
}: FlowComponentProps) => {
  const [fitViewOnInit, setFitViewOnInit] = useState(true);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [nodeToDelete, setNodeToDelete] = useState<any>(null);
  
  // Reset fitViewOnInit whenever nodes or edges change significantly
  useEffect(() => {
    if (nodes.length > 0 || edges.length > 0) {
      setFitViewOnInit(true);
    }
  }, [nodes.length, edges.length]);

  // Handle nodes change with proper account owner logic
  const handleNodesChange = useCallback(async (changes: any) => {
    // Check for node deletion
    const deletions = changes.filter((change: any) => change.type === 'remove');
    
    if (deletions.length > 0) {
      // If there's a node to delete, show confirmation dialog
      const nodeId = deletions[0].id;
      const nodeData = nodes.find(node => node.id === nodeId)?.data;
      
      if (nodeData) {
        // Stop the deletion and show confirmation dialog
        setNodeToDelete({
          id: nodeId,
          data: nodeData,
          changes: changes.filter(c => c.id !== nodeId || c.type !== 'remove')
        });
        setShowDeleteConfirmation(true);
        
        // Filter out the deletion action to prevent immediate deletion
        const nonDeletionChanges = changes.filter(c => !(c.id === nodeId && c.type === 'remove'));
        onNodesChange(nonDeletionChanges);
        return;
      }
    }
    
    // Handle other changes (e.g., position changes)
    onNodesChange(changes);
    
    // Only save positions after dragging is complete
    const positionChanges = changes.filter((change: any) => 
      change.type === 'position' && change.dragging === false
    );
    
    if (positionChanges.length > 0 && selectedAccount?.company_id) {
      try {
        // Get current user
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        
        // Get account ownership info
        const ownershipInfo = await getAccountOwnershipInfo(selectedAccount.company_id);
        if (!ownershipInfo.canEdit) {
          toast.error("Only account owners can move nodes");
          return;
        }
        
        // Save each changed node's position to the database
        for (const change of positionChanges) {
          // Find the node data to get title and description for matching
          const changedNode = nodes.find(node => node.id === change.id);
          if (!changedNode || !changedNode.data) continue;
          
          const title = String(changedNode.data.title || '');
          const description = String(changedNode.data.description || '');
          
          console.log('Updating node position for:', {
            nodeId: change.id,
            title,
            description,
            newPosition: change.position
          });
          
          // Update the node position in Supabase using account owner's user_id
          const { error } = await supabase
            .from('business_value_framework')
            .update({
              position_x: change.position.x,
              position_y: change.position.y
            })
            .eq('user_id', ownershipInfo.accountOwnerUserId)
            .eq('company_id', selectedAccount.company_id)
            .eq('title', title)
            .eq('description', description);
            
          if (error) {
            console.error('Error updating node position from Flow component:', error);
            toast.error("Failed to save node position");
          } else {
            console.log('Successfully saved position for node with title:', title);
          }
        }
      } catch (error) {
        console.error('Error updating node positions from Flow component:', error);
        toast.error("Failed to save node positions");
      }
    }
  }, [onNodesChange, selectedAccount, nodes]);

  // Handle edges change with proper account owner logic
  const handleEdgesChange = useCallback(async (changes: any) => {
    // Call the parent handler first
    onEdgesChange(changes);
    
    if (selectedAccount?.company_id) {
      // Check if there are any edge removals
      const hasEdgeChanges = changes.some((change: any) => change.type === 'remove');
      
      if (hasEdgeChanges) {
        try {
          // Get current user
          const { data: { user } } = await supabase.auth.getUser();
          if (!user) return;
          
          // Get account ownership info
          const ownershipInfo = await getAccountOwnershipInfo(selectedAccount.company_id);
          if (!ownershipInfo.canEdit) {
            toast.error("Only account owners can modify connections");
            return;
          }
          
          // Get updated edges after the parent handler has processed them
          const updatedEdges = [...edges].filter(edge => 
            !changes.some((change: any) => 
              change.type === 'remove' && change.id === edge.id
            )
          );
          
          // For each node that is connected with these edges, update the edges field
          // First, extract unique node IDs from edges
          const affectedSourceNodeIds = new Set<string>();
          updatedEdges.forEach(edge => {
            affectedSourceNodeIds.add(edge.source.replace('kpi-', ''));
          });
          
          // Update each affected node's edges field using account owner's user_id
          for (const nodeId of affectedSourceNodeIds) {
            // Get edges connected to this node
            const nodeEdges = updatedEdges.filter(edge => 
              edge.source === `kpi-${nodeId}` || edge.target === `kpi-${nodeId}`
            );
            
            // Find the node to get its title and description for matching
            const nodeData = nodes.find(node => node.id === `kpi-${nodeId}`)?.data;
            const title = String(nodeData?.title || '');
            const description = String(nodeData?.description || '');
            
            // Ensure edges are properly serialized for Supabase
            const serializableEdges = JSON.parse(JSON.stringify(nodeEdges));
            
            const { error } = await supabase
              .from('business_value_framework')
              .update({ edges: serializableEdges })
              .eq('user_id', ownershipInfo.accountOwnerUserId)
              .eq('company_id', selectedAccount.company_id)
              .eq('title', title)
              .eq('description', description);
              
            if (error) {
              console.error('Error saving edges from Flow component:', error);
              toast.error("Failed to save edge changes");
            }
          }
        } catch (error) {
          console.error('Error saving edges from Flow component:', error);
          toast.error("Failed to save edge changes");
        }
      }
    }
  }, [edges, onEdgesChange, selectedAccount, nodes]);

  // Handle new connections with proper account owner logic
  const handleConnect = useCallback(async (params: Connection) => {
    // Call the parent handler first
    onConnect(params);
    
    if (selectedAccount?.company_id) {
      try {
        // Get current user
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        
        // Get account ownership info
        const ownershipInfo = await getAccountOwnershipInfo(selectedAccount.company_id);
        if (!ownershipInfo.canEdit) {
          toast.error("Only account owners can create connections");
          return;
        }
        
        // Create the new edge
        const newEdge = { 
          id: `e-${params.source}-${params.target}`,
          source: params.source,
          target: params.target,
          sourceHandle: params.sourceHandle,
          targetHandle: params.targetHandle
        };
        
        // Get updated edges including the new one
        const updatedEdges = [...edges, newEdge];
        
        // Determine which nodes are affected by this connection
        const sourceNodeId = params.source;
        const targetNodeId = params.target;
        
        // Update the source node's edges using account owner's user_id
        const sourceNodeEdges = updatedEdges.filter(edge => 
          edge.source === params.source || edge.target === params.source
        );
        
        // Find the node to get its title and description for matching
        const sourceNodeData = nodes.find(node => node.id === params.source)?.data;
        const title = String(sourceNodeData?.title || '');
        const description = String(sourceNodeData?.description || '');
        
        // Ensure edges are properly serialized for Supabase
        const serializableEdges = JSON.parse(JSON.stringify(sourceNodeEdges));
        
        const { error } = await supabase
          .from('business_value_framework')
          .update({ edges: serializableEdges })
          .eq('user_id', ownershipInfo.accountOwnerUserId)
          .eq('company_id', selectedAccount.company_id)
          .eq('title', title)
          .eq('description', description);
          
        if (error) {
          console.error('Error saving source node connection:', error);
          toast.error("Failed to save connection to source node");
        }
        
        // Also update the target node's edges using account owner's user_id
        const targetNodeEdges = updatedEdges.filter(edge => 
          edge.source === params.target || edge.target === params.target
        );
        
        // Find the node to get its title and description for matching
        const targetNodeData = nodes.find(node => node.id === params.target)?.data;
        const targetTitle = String(targetNodeData?.title || '');
        const targetDescription = String(targetNodeData?.description || '');
        
        // Ensure edges are properly serialized for Supabase
        const serializableTargetEdges = JSON.parse(JSON.stringify(targetNodeEdges));
        
        const { error: targetError } = await supabase
          .from('business_value_framework')
          .update({ edges: serializableTargetEdges })
          .eq('user_id', ownershipInfo.accountOwnerUserId)
          .eq('company_id', selectedAccount.company_id)
          .eq('title', targetTitle)
          .eq('description', targetDescription);
          
        if (targetError) {
          console.error('Error saving target node connection:', targetError);
          toast.error("Failed to save connection to target node");
        }
      } catch (error) {
        console.error('Error saving new connection from Flow component:', error);
        toast.error("Failed to save connection");
      }
    }
  }, [edges, onConnect, selectedAccount, nodes]);

  // Handle node deletion confirmation
  const handleConfirmDelete = async () => {
    if (!nodeToDelete || !selectedAccount?.company_id) {
      setShowDeleteConfirmation(false);
      return;
    }
    
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get account ownership info
      const ownershipInfo = await getAccountOwnershipInfo(selectedAccount.company_id);
      if (!ownershipInfo.canEdit) {
        toast.error("Only account owners can delete nodes");
        setShowDeleteConfirmation(false);
        return;
      }

      const title = String(nodeToDelete.data?.title || '');
      const description = String(nodeToDelete.data?.description || '');
      
      // Delete the record from Supabase using account owner's user_id
      const { error } = await supabase
        .from('business_value_framework')
        .delete()
        .eq('user_id', ownershipInfo.accountOwnerUserId)
        .eq('company_id', selectedAccount.company_id)
        .eq('title', title)
        .eq('description', description);
        
      if (error) {
        console.error('Error deleting node from Supabase:', error);
        toast.error("Failed to delete node from database");
      } else {
        toast.success("Node deleted successfully");
        
        // Now apply the deletion to the UI
        const deleteChange = { type: 'remove', id: nodeToDelete.id };
        onNodesChange([deleteChange]);
      }
    } catch (error) {
      console.error('Error deleting node:', error);
      toast.error("Failed to delete node");
    } finally {
      setShowDeleteConfirmation(false);
      setNodeToDelete(null);
    }
  };

  // Handle cancel delete
  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
    setNodeToDelete(null);
  };

  return (
    <>
      <div style={{
        height: `${flowHeight}px`,
        width: '100%',
        overflow: 'auto'
      }}>
        <ReactFlow 
          nodes={nodes.map(node => ({
            ...node,
            data: {
              ...node.data,
              companyId: selectedAccount?.company_id
            }
          }))} 
          edges={edges} 
          onNodesChange={handleNodesChange} 
          onEdgesChange={handleEdgesChange}
          onConnect={handleConnect}
          nodeTypes={nodeTypes} 
          nodesDraggable={true} 
          elementsSelectable={true} 
          selectNodesOnDrag={false} 
          panOnDrag={true} 
          connectionMode={ConnectionMode.Loose}
          onSelectionChange={onSelectionChange}
          proOptions={{
            hideAttribution: true
          }} 
          snapToGrid={false} 
          snapGrid={[15, 15]} 
          defaultViewport={{
            x: 0,
            y: 0,
            zoom: 1
          }} 
          fitView={fitViewOnInit}
          onInit={() => {
            // After first fitting the view, don't automatically fit again
            setTimeout(() => setFitViewOnInit(false), 100);
          }}
          fitViewOptions={{
            padding: 0.2,
            includeHiddenNodes: false,
            maxZoom: 1.5
          }}
          minZoom={0.1}
        >
          <Background />
          <Controls />
        </ReactFlow>
      </div>
      
      {/* Confirmation Dialog for Node Deletion */}
      <AlertDialog open={showDeleteConfirmation} onOpenChange={setShowDeleteConfirmation}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Node</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this node? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelDelete}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
});

FlowComponent.displayName = "FlowComponent";

export default FlowComponent;
